import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import {
  activationCameraKey,
  describeIncidentStep,
  type IncidentActivation,
  type IncidentCall,
  type IncidentDetail,
} from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { Empty, Field, InlineLink, Section } from "./DetailSection";

interface IncidentDetailViewProps {
  readonly incident: IncidentDetail;
  readonly tenantLabel: string;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** The activation page, whose operator notes include every call made on it. */
  readonly activationHref: (ticketNo: string) => Href;
  /** The Patriot client's page. */
  readonly clientHref: Href;
  /** The fleet incident list narrowed to this incident's client. */
  readonly clientIncidentsHref: Href;
  /** Where a call outside any activation can be read: no activation page holds its note. */
  readonly ticketlessCallHref: (call: IncidentCall) => Href;
}

/**
 * Why an incident counts, as a timeline from the rule's own stored evidence,
 * then the calls and activations it is made of. A call is shown by its note
 * id only: who was called is in the operator note, on the activation's page,
 * or, for a call outside any activation, in the client's operator notes.
 */
export function IncidentDetailView({
  incident,
  tenantLabel,
  siteLabel,
  cameraLabel,
  activationHref,
  clientHref,
  clientIncidentsHref,
  ticketlessCallHref,
}: IncidentDetailViewProps) {
  const cameraKey = activationCameraKey(incident);

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Started">{formatInstant(incident.startedAt)}</Field>
        <Field label="Ended">{formatInstant(incident.endedAt)}</Field>
        <Field label="Police">{incident.policeCalled ? "Called" : "Not called"}</Field>
        <Field label="Status">
          {incident.settled ? "Settled" : "Open: more calls or activations may still join it"}
        </Field>
        <Field label="Patriot client">
          <View flexDirection="row" gap="lg" flexWrap="wrap">
            <InlineLink href={clientHref}>{incident.clientNo}</InlineLink>
            <InlineLink href={clientIncidentsHref}>Other incidents for this client</InlineLink>
          </View>
        </Field>
        <Field label="Organisation">{tenantLabel}</Field>
        <Field label="Site">{incident.a3sSite !== undefined ? siteLabel(incident.a3sSite) : EMPTY}</Field>
        <Field label="Camera">{cameraKey !== undefined ? cameraLabel(cameraKey) : EMPTY}</Field>
      </Section>

      <Section title="Why it counts">
        {incident.steps.map((step, index) => {
          const described = describeIncidentStep(step, incident);
          return (
            <View key={`${step.kind}#${String(index)}`} style={[styles.entry, index > 0 && styles.divider]}>
              <Text textStyle="xs/regular" color="textMuted">
                {formatInstant(step.at)}
              </Text>
              {described.ticketNo !== undefined ?
                <InlineLink href={activationHref(described.ticketNo)}>{described.title}</InlineLink>
              : <Text textStyle="sm/semibold" color="text">
                  {described.title}
                </Text>
              }
              {described.detail !== undefined && (
                <Text textStyle="sm/regular" color="textMuted">
                  {described.detail}
                </Text>
              )}
            </View>
          );
        })}
      </Section>

      <Section title={`Calls (${String(incident.calls.length)})`}>
        {incident.calls.length === 0 ?
          <Empty>No calls.</Empty>
        : incident.calls.map((call, index) => (
            <CallRow
              key={call.noteId}
              call={call}
              isFirst={index === 0}
              href={call.ticketNo !== undefined ? activationHref(call.ticketNo) : ticketlessCallHref(call)}
            />
          ))
        }
      </Section>

      <Section title={`Activations (${String(incident.activations.length)})`}>
        {incident.activations.length === 0 ?
          <Empty>The calls were made outside any activation.</Empty>
        : incident.activations.map((activation, index) => (
            <ActivationRow
              key={activation.ticketNo}
              activation={activation}
              isFirst={index === 0}
              href={activationHref(activation.ticketNo)}
            />
          ))
        }
      </Section>
    </View>
  );
}

function CallRow({ call, isFirst, href }: { call: IncidentCall; isFirst: boolean; href: Href }) {
  return (
    <View style={[styles.entry, !isFirst && styles.divider]}>
      <Text textStyle="xs/regular" color="textMuted">
        {formatInstant(call.at)}
      </Text>
      <Text textStyle="sm/regular" color="text">
        {[call.direction === "outbound" ? "Outbound" : "Inbound", call.police ? "police" : undefined]
          .filter((part) => part !== undefined)
          .join(", ")}
      </Text>
      <InlineLink href={href}>
        {call.ticketNo !== undefined ?
          `Note ${call.noteId} on activation ${call.ticketNo}`
        : `Note ${call.noteId}, outside any activation`}
      </InlineLink>
    </View>
  );
}

function ActivationRow({
  activation,
  isFirst,
  href,
}: {
  activation: IncidentActivation;
  isFirst: boolean;
  href: Href;
}) {
  return (
    <View style={[styles.entry, !isFirst && styles.divider]}>
      <InlineLink href={href}>{`Activation ${activation.ticketNo}`}</InlineLink>
      <Field label="Opened">{formatInstant(activation.openedAt)}</Field>
      <Field label="Closed">{activation.closedAt !== undefined ? formatInstant(activation.closedAt) : "Open"}</Field>
      <Field label="Reason">{formatText(activation.reason)}</Field>
      <Field label="In because">
        {activation.link.kind === "called" ? "A call was made on it" : "Open near a call"}
      </Field>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  divider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
}));
